/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable array-callback-return */
import { Modal } from "react-bootstrap";
import React, { useEffect, useRef, useState } from "react";
import { FormControl, MenuItem, Select } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useFormik } from "formik";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import SnackBarTool from "../../../../../../_metronic/_partials/widgets/snack-bar/SnackBar";

import DiscardDialog from "../../../../../../_metronic/_partials/widgets/alert/DiscardDialog";
import { LoadingCustome } from "../../../../../../_metronic/_partials/controls/LoadingCustome";
import {
  automatePrice,
  createSite,
} from "../../../_redux/franchises/franchisesCrud";
import { SiteInstructionsSection } from "./SiteInstructionsSection";
import { AutomateValidation } from "../franchise-form/validateSchema";
import {
  fetchFranchisesDetail,
  fetchSiteList,
} from "../../../_redux/franchises/franchisesActions";
import { useIntl } from "react-intl";
import ImageUploadingModule from "react-images-uploading";
import { InputGroup } from "react-bootstrap";
import DatePicker from "react-datepicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import moment from "moment";
import _ from "lodash";

const ImageUploading = ImageUploadingModule.default ?? ImageUploadingModule;

/**
 * Merges create-site API output and submitted form values into `dialog.data` so Formik
 * `enableReinitialize` does not clear the panel after the first save (instructions flow).
 *
 * @param {object|undefined} prevData - Prior `dialog.data`.
 * @param {object} formValues - Formik values from submit (includes `images` file list).
 * @param {string|number} newId - Newly created site id.
 * @param {object|null|undefined} apiData - `res.data.data` when the API returns an object.
 * @returns {object} Next `dialog.data` snapshot.
 */
function buildDialogDataAfterCreate(prevData, formValues, newId, apiData) {
  // `images` is dropped: server media (or prior dialog.data) is the source of truth
  // post-save. Holding `File` objects here would cause them to be resubmitted on the
  // next PATCH via the useEffect that hydrates formik.values.images.
  const { images: _formImages, ...scalarValues } = formValues;
  const base =
    apiData && typeof apiData === "object" && !Array.isArray(apiData)
      ? { ...apiData }
      : {};

  const serverImages = Array.isArray(base.image) ? base.image : null;
  const prevImages = Array.isArray(prevData?.image) ? prevData.image : null;
  const hasServerImages = Boolean(serverImages?.length);

  const image = hasServerImages ? serverImages : serverImages || prevImages;

  const next = {
    ...(prevData || {}),
    ...base,
    ...scalarValues,
    id: newId,
    image,
  };
  delete next.pendingSiteImages;
  return next;
}

/**
 * Right-panel (offcanvas) form to create or edit a franchise site, including capacities,
 * manager details, lock type, gallery images, and site instructions when a site id exists.
 *
 * @param {object} props - Component props.
 * @param {{ check: boolean, data?: object }} props.dialog - Panel visibility and optional site row / merged create payload (`data.id`, fields for edit).
 * @param {function} props.setDialog - Parent state setter for `dialog` (object or updater); closes panel and merges ids after create when needed.
 * @param {string|number} [props.franchiseId] - Franchise identifier if passed separately from `franchData` (reserved / optional for callers).
 * @param {object} props.franchData - Current franchise record (expects at least `id` for `franchise_id` and list refreshes).
 * @returns {JSX.Element} Site add/edit offcanvas UI.
 */
const SiteModal = ({ dialog, setDialog, franchiseId, franchData }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [snack, setSnack] = useState({ call: false, type: "", message: "" });
  const [dialogDis, setDialogDis] = useState(false);
  /** Gallery files pending upload (Formik + ImageUploading); never server metadata objects. */
  const [images, setImages] = React.useState([]);
  /** Existing site images from API (`dialog.data.image`); preview only, not submitted as Files. */
  const [serverImages, setServerImages] = useState([]);
  const maxNumber = 69;
  const imageConvertGenRef = useRef(0);
  const isDismissedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const initialValues = {
    franchise_id: franchData?.id,
    name: dialog?.data?.name || "",
    capacity: dialog?.data?.capacity || "",
    _20ft_capacity: dialog?.data?._20ft_capacity || "",
    _5ft_capacity: dialog?.data?._5ft_capacity || "",
    _6ft_capacity: dialog?.data?._6ft_capacity || "",
    _8ft_capacity: dialog?.data?._8ft_capacity || "",
    car_parking_capacity: dialog?.data?.car_parking_capacity || "",
    manager_name: dialog?.data?.manager_name || "",
    manager_email: dialog?.data?.manager_email || "",
    manager_phone: dialog?.data?.manager_phone || "",
    address: dialog?.data?.address || "",
    latitude: dialog?.data?.latitude || "",
    longitude: dialog?.data?.longitude || "",
    available_from: dialog?.data?.available_from || "",
    is_default: dialog?.data?.is_default || "",
    additional_email_address: dialog?.data?.additional_email_address || "",
    key_holder_responder_name: dialog?.data?.key_holder_responder_name || "",
    key_holder_responder_number:
      dialog?.data?.key_holder_responder_number || "",
    access_control_emergency_name:
      dialog?.data?.access_control_emergency_name || "",
    access_control_emergency_number:
      dialog?.data?.access_control_emergency_number || "",
    images:
      dialog?.data?.pendingSiteImages?.length > 0
        ? dialog.data.pendingSiteImages
        : [],
    lock_type: dialog?.data?.lock_type || "Lock Rental",
  };

  /**
   * Resolves a site id from a create-site API response when the backend nests or returns a scalar id.
   *
   * @param {object} res - Axios response.
   * @returns {string|number|null} New site id or null.
   */
  const parseNewSiteIdFromResponse = (res) => {
    const d = res?.data?.data;
    if (d != null && typeof d === "object") {
      return d.id ?? d.site_id ?? d.site?.id ?? null;
    }
    if (typeof d === "number" || typeof d === "string") {
      return d;
    }
    const top = res?.data?.id;
    if (typeof top === "number" || typeof top === "string") {
      return top;
    }
    return null;
  };

  const handleSubmit = (values) => {
    setIsLoading(true);
    const isEditFlow = Boolean(dialog?.data?.id);
    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      // Handle the 'images' separately, as it is an array of files
      if (key === "images") {
        values[key]
          .filter((file) => file instanceof File || file instanceof Blob)
          .forEach((file, index) => {
            formData.append(`${key}[${index}]`, file);
          });
      } else {
        formData.append(key, values[key]);
      }
    });
    setSnack({ call: false, type: "", message: "" });
    createSite(
      formData,
      dialog?.data ? "/" + dialog?.data.id + "?_method=PATCH" : ""
    )
      .then((res) => {
        setIsLoading(false);
        dispatch(fetchSiteList(franchData?.id));
        dispatch(fetchFranchisesDetail(values?.franchise_id));
        setSnack({ call: true, type: "success", message: res.data.message });

        let stayOpenForInstructions = false;
        if (!isEditFlow && !isDismissedRef.current) {
          const newId = parseNewSiteIdFromResponse(res);
          if (newId != null) {
            stayOpenForInstructions = true;
            const apiData = res?.data?.data;
            setDialog((prev) => ({
              ...prev,
              check: true,
              data: buildDialogDataAfterCreate(
                prev.data,
                values,
                newId,
                apiData
              ),
            }));
          }
        }

        if (!stayOpenForInstructions) {
          setTimeout(() => {
            setDialog({ check: false });
            setSnack({ call: false, type: "", message: "" });
            formik.resetForm();
          }, 3000);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setSnack({
          call: true,
          type: "error",
          message:
            error?.response?.data?.message ??
            error?.message ??
            "Could not save site.",
        });
      });
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    // validationSchema: AutomateValidation,
    onSubmit: handleSubmit,
  });
  const onChange = (imageList, addUpdateIndex) => {
    // data for submit

    const arr = imageList.map((e) => e?.file || e);
    setImages(arr);

    formik.setFieldValue("images", arr);
  };
  const handleClose = () => {
    isDismissedRef.current = true;
    formik.resetForm();
    setImages([]);
    setServerImages([]);

    setDialog({ check: false });
  };
  useEffect(() => {
    const bumpGen = () => {
      imageConvertGenRef.current += 1;
    };
    imageConvertGenRef.current += 1;

    const serverList = Array.isArray(dialog?.data?.image)
      ? dialog.data.image
      : [];

    if (dialog?.data?.pendingSiteImages?.length > 0) {
      const files = dialog.data.pendingSiteImages;
      setImages(files);
      formik.setFieldValue("images", files);
      setServerImages(serverList);
      return bumpGen;
    }

    const hasPending = Boolean(dialog?.data?.pendingSiteImages?.length);
    const hasServerImages = Boolean(serverList.length);
    if (!hasPending && !hasServerImages) {
      setImages([]);
      setServerImages([]);
      formik.setFieldValue("images", []);
      return bumpGen;
    }

    if (hasServerImages) {
      setServerImages(serverList);
      setImages([]);
      formik.setFieldValue("images", []);
    }
    return bumpGen;
  }, [dialog?.data?.image, dialog?.data?.pendingSiteImages]);

  return (
    <div
      id="kt_site_panel"
      className="offcanvas offcanvas-right pt-5 pb-10 offcanvas-on "
    >
      {/*begin::Header*/}
      <div className="offcanvas-header offcanvas-header-navs d-flex align-items-center justify-content-between mb-5">
        <div
          className="offcanvas-close mt-n1 pr-5"
          style={{ position: "absolute", top: "15px", right: "10px" }}
        >
          <span
            onClick={handleClose}
            className="btn btn-xs btn-icon btn-light btn-hover-primary"
            id="kt_site_panel_close"
          >
            <i className="ki ki-close icon-sm text-muted"></i>
          </span>
        </div>
      </div>
      {/*end::Header*/}
      <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
        <div className="offcanvas-content px-10 date-picker ">
          <div>
            <h5>Site</h5>
          </div>
          <div className="tab-content">
            <div
              id="kt_quick_panel_logs_kt_site_panel"
              className="tab-pane fade pt-3 pr-5 mr-n5 custom-scroll  active show"
              role="tabpanel"
            >
              <div className="mt-10 pb-10">
                <div className="row">
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Site Name</span>
                    <input
                      type="text"
                      name="name"
                      placeholder={"Site Name"}
                      label="External Name"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("name")}
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.name}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">20ft Capacity​</span>
                    <input
                      type="text"
                      name="_20ft_capacity"
                      placeholder={"20ft Capacity​"}
                      label="20ft Capacity​"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("_20ft_capacity")}
                    />
                    {formik.touched._20ft_capacity &&
                    formik.errors._20ft_capacity ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors._20ft_capacity}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">5ft Capacity</span>
                    <input
                      type="text"
                      name="_5ft_capacity"
                      placeholder={"5ft Capacity"}
                      label="Capacity"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("_5ft_capacity")}
                    />
                    {formik.touched._5ft_capacity &&
                    formik.errors._5ft_capacity ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors._5ft_capacity}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">6ft Capacity</span>
                    <input
                      type="text"
                      name="_6ft_capacity"
                      placeholder={"6ft Capacity"}
                      label="6ft Capacity"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("_6ft_capacity")}
                    />
                    {formik.touched._6ft_capacity &&
                    formik.errors._6ft_capacity ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors._6ft_capacity}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">8ft Capacity​</span>
                    <input
                      type="text"
                      name="_8ft_capacity"
                      placeholder={"8ft Capacity​"}
                      label="8ft Capacity​"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("_8ft_capacity")}
                    />
                    {formik.touched._8ft_capacity &&
                    formik.errors._8ft_capacity ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors._8ft_capacity}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Car Parking Capacity</span>
                    <input
                      type="text"
                      name="car_parking_capacity"
                      placeholder={"Car Parking Capacity"}
                      label="Car Parking Capacity"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("car_parking_capacity")}
                    />
                    {formik.touched.car_parking_capacity &&
                    formik.errors.car_parking_capacity ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.car_parking_capacity}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Manager Name</span>
                    <input
                      type="text"
                      name="manager_name"
                      placeholder={"Manager Name"}
                      label="External Name"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("manager_name")}
                    />
                    {formik.touched.manager_name &&
                    formik.errors.manager_name ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.manager_name}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Manager Email</span>
                    <input
                      type="text"
                      name="manager_email"
                      placeholder={"Manager Email"}
                      label="External Name"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("manager_email")}
                    />
                    {formik.touched.manager_email &&
                    formik.errors.manager_email ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.manager_email}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Manager Phone</span>
                    <input
                      type="text"
                      name="manager_phone"
                      placeholder={"Manager Phone"}
                      label="External Name"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("manager_phone")}
                    />
                    {formik.touched.manager_phone &&
                    formik.errors.manager_phone ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.manager_phone}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Site Address</span>
                    <input
                      type="text"
                      name="address"
                      placeholder={"Site Address"}
                      label="External Name"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("address")}
                    />
                    {formik.touched.address && formik.errors.address ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.address}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-md-6 mb-5">
                    <label>{intl.formatMessage({ id: "From.Date" })}</label>
                    <InputGroup className="calendr">
                      <DatePicker
                        fullWidth
                        fixedHeight
                        className="form-control"
                        wrapperClassName="datepicker"
                        selected={Date.parse(formik?.values.available_from)}
                        // startDate={Date.parse(formik?.values.blocking_date_from)}
                        // endDate={Date.parse(formik?.values.blocking_date_to)}
                        dateFormat="dd-MM-Y"
                        placeholderText="Select From Date"
                        onChange={(fromDate) => {
                          formik.setFieldValue(
                            `available_from`,
                            moment(fromDate).format("yyyy-MM-DD")
                          );
                        }}
                        minDate={new Date()}
                      />
                      <CalendarTodayIcon />
                    </InputGroup>
                    {formik.touched.blocking_date_from &&
                    formik.errors.blocking_date_from ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.blocking_date_from}
                        </div>
                      </div>
                    ) : null}
                    {formik.touched.blocking_date_to &&
                    !formik.errors.blocking_date_from &&
                    formik.errors.blocking_date_to ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.blocking_date_to}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Lat</span>
                    <input
                      type="text"
                      name="latitude"
                      placeholder={"Lat"}
                      label="External Name"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("latitude")}
                    />
                    {formik.touched.latitude && formik.errors.latitude ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.latitude}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Long</span>
                    <input
                      type="text"
                      name="longitude"
                      placeholder={"Long"}
                      label="External Name"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("longitude")}
                    />
                    {formik.touched.longitude && formik.errors.longitude ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.longitude}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Additional Email Address</span>
                    <input
                      type="text"
                      name="additional_email_address"
                      placeholder={"Email"}
                      label="Email"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("additional_email_address")}
                    />
                    {formik.touched.additional_email_address &&
                    formik.errors.additional_email_address ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.additional_email_address}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Key Holder Responder Name</span>
                    <input
                      type="text"
                      name="key_holder_responder_name"
                      placeholder={"name"}
                      label="name"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("key_holder_responder_name")}
                    />
                    {formik.touched.key_holder_responder_name &&
                    formik.errors.key_holder_responder_name ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.key_holder_responder_name}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Key Holder Responder Number</span>
                    <input
                      type="text"
                      name="key_holder_responder_number"
                      placeholder={"Number"}
                      label="Number"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("key_holder_responder_number")}
                    />
                    {formik.touched.key_holder_responder_number &&
                    formik.errors.key_holder_responder_number ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.key_holder_responder_number}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">Access Control Emergency Name</span>
                    <input
                      type="text"
                      name="access_control_emergency_name"
                      placeholder={"Name"}
                      label="Name"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps("access_control_emergency_name")}
                    />
                    {formik.touched.access_control_emergency_name &&
                    formik.errors.access_control_emergency_name ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.access_control_emergency_name}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6 mb-4">
                    <span className="mt-3 ">
                      Access Control Emergency Number
                    </span>
                    <input
                      type="text"
                      name="access_control_emergency_number"
                      placeholder={"Number"}
                      label="Number"
                      className={`form-control  `}
                      // max={3}
                      // maxLength="3"
                      {...formik.getFieldProps(
                        "access_control_emergency_number"
                      )}
                    />
                    {formik.touched.access_control_emergency_number &&
                    formik.errors.access_control_emergency_number ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.access_control_emergency_number}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-12 mb-4">
                    <span className="mt-3">Lock Type</span>
                    <div>
                      <label style={{ marginRight: "15px" }}>
                        <input
                          type="radio"
                          name="lock_type"
                          value="Lock Rental"
                          checked={formik.values.lock_type === "Lock Rental"}
                          onChange={(e) =>
                            formik.setFieldValue("lock_type", e.target.value)
                          }
                          style={{
                            marginRight: "5px",
                            cursor: "pointer",
                          }}
                        />
                        Lock Rental
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="lock_type"
                          value="Bluetooth Lock"
                          checked={formik.values.lock_type === "Bluetooth Lock"}
                          onChange={(e) =>
                            formik.setFieldValue("lock_type", e.target.value)
                          }
                          style={{
                            marginRight: "5px",
                            cursor: "pointer",
                          }}
                        />
                        Bluetooth Lock
                      </label>
                    </div>
                    {formik.touched.lock_type && formik.errors.lock_type ? (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          {formik.errors.lock_type}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="col-12 mb-4">
                    {serverImages.length > 0 ? (
                      <div className="mb-3">
                        <span className="text-muted d-block mb-2">
                          Existing images
                        </span>
                        <div
                          className="d-flex flex-wrap"
                          style={{ gap: "12px" }}
                        >
                          {serverImages.map((img, index) => (
                            <img
                              key={img?.id ?? `server-img-${index}`}
                              src={img?.image}
                              alt=""
                              width="100"
                              height="100"
                              style={{ objectFit: "cover" }}
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <ImageUploading
                      multiple
                      value={images}
                      onChange={onChange}
                      maxNumber={maxNumber}
                      dataURLKey="data_url"
                      acceptType={["jpg", "png"]}
                    >
                      {({
                        imageList,
                        onImageUpload,
                        onImageRemoveAll,
                        onImageUpdate,
                        onImageRemove,
                        isDragging,
                        dragProps,
                      }) => (
                        // write your building UI
                        (<div className="upload__image-wrapper">
                          <button
                            type="button"
                            className="btn btn-dark font-weight-bolder font-size-sm mr-3 "
                            style={isDragging ? { color: "red" } : null}
                            onClick={onImageUpload}
                            {...dragProps}
                          >
                            Click or Drop here
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary font-weight-bolder font-size-sm "
                            onClick={onImageRemoveAll}
                          >
                            Remove all images
                          </button>
                          {imageList.map((image, index) => (
                            <div key={index} className="image-item">
                              <img
                                src={URL.createObjectURL(image)}
                                alt=""
                                width="100"
                              />
                              <div className="image-item__btn-wrapper">
                                <button
                                  type="button"
                                  className="btn btn-dark font-weight-bolder font-size-sm mr-3 "
                                  onClick={() => onImageUpdate(index)}
                                >
                                  Update
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-primary font-weight-bolder font-size-sm "
                                  onClick={() => onImageRemove(index)}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>)
                      )}
                    </ImageUploading>
                  </div>
                  {dialog?.data?.id ? (
                    <div className="col-12">
                      <SiteInstructionsSection
                        key={`instructions-site-${dialog.data.id}`}
                        entityId={dialog.data.id}
                        setSnack={setSnack}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="offcanvas-footer">
          <div className="offcanvas-content text-right ">
            <button
              type="reset"
              className="btn btn-dark font-weight-bolder font-size-sm mr-3 "
              onClick={handleClose}
            >
              {intl.formatMessage({ id: "CANCEL" })}
            </button>
            <button
              type="submit"
              className="btn btn-primary font-weight-bolder font-size-sm "
              disabled={isLoading}
            >
              {intl.formatMessage({ id: "SAVE" })}
              {isLoading && (
                <span className="ml-1 spinner spinner-white"></span>
              )}
            </button>
          </div>
        </div>
      </form>
      {snack.call ? <SnackBarTool {...snack} /> : ""}
    </div>
  );
};

export default SiteModal;
